// Preload this for Firefox
import { InterpolatedView, Interpolation } from './interpolate';
import { CanvasRendererFactory, CanvasState } from './canvasrender';
import { CanvasAndroid, ObjectId, ObjectIdMap, Point, Rect, View } from './view';
import { Matrix } from './matrix';
import {
  CanvasOpAndroid,
  CanvasOpIOS,
  Flags,
  FSWebviewNotRecordedReason,
} from '@fullstory/mobile-flatbuffers';
import { MobileDriver } from './driver';
import { PageTypeEnum } from '@fullstory/client-api';
import { WebViewsState } from './webbundle';
import { SelectorChain, SelectorPart } from '@fullstory/proto/fs/selectors';
import { ObjectCollection, ObjectPathDepth, ViewPath } from '@fullstory/renderer-shared';
import { SelectorMatcher, newSelectorChain } from '@fullstory/selectors';
import { OffscreenCanvasCache } from './offscreencanvascache';
import { logWarnOnce, logErrorOnce, logOnce } from './log_once';
import { intl } from '@fullstory/intl';

const BLOCKED_IMG = new Image();
BLOCKED_IMG.src =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACEAAAAhCAYAAABX5MJvAAAAzUlEQVRYCcXWyQ7CMA' +
  'wE0KQq2/9/KUJFUERbBolKpZvteJl7rBf7Mvl6a/pPUmku51M6Hmr2mHfXpSoa0NwfqWLT/x6UbAAALKEIoQHAn8QILYAYoQkQIbQBbIQFgIWwApARl' +
  'gASwhqwi/AAbCK8AKsIT8AiwhswQ0QAJogowIiIBACRn+2rlzaiXx/AoJJkSbdDJdMC4ArsPqENwBVYCAsAzkhGWAHICEsACWEN2EV4ADYRXoBVhCdg' +
  'EeENmCEiABNEFGBERAK+iGgAEAMYSDIGzIyangAAAABJRU5ErkJggg==';

const LOGGING_INTERVAL = 10000;

export class ViewMatch {
  // The view's path, for use with queryObjects() and related APIs.
  path: ViewPath;
  // The view's onscreen rectangular size
  size: number;
  // The view's parent hierarchy
  viewSelector: SelectorPart[];
  view: View;

  constructor(view: View, selector: SelectorPart[], size: number) {
    this.path = new ViewPath(view.uniqueId);
    this.size = size;
    this.view = view;
    this.viewSelector = selector;
  }

  static same(v1: ViewMatch, v2: ViewMatch): boolean {
    if (!v1 || !v2) {
      return false;
    }

    if (!v1.view || !v2.view) {
      return false;
    }

    return v1.view.uniqueId == v2.view.uniqueId;
  }

  // Assumes an array with the top-most view at the start and leaf-most view at the end.
  // Creates a SelectorRule chain that shares the same underlying properties but with a parent chain in place.
  private static selectorChainFromArray(rules: SelectorPart[]): SelectorChain | null {
    if (rules.length === 0) {
      return null;
    }

    const r = newSelectorChain(ViewMatch.selectorChainFromArray(rules.slice(0, -1)), ' ');
    const cur = rules[rules.length - 1];
    r.rule.tag = cur.tag;
    r.rule.id = cur.id;
    r.rule.classes = cur.classes;
    r.rule.attrs = cur.attrs;
    r.rule.attrMatch = cur.attrMatch;
    if (!r.parent) {
      r.combinator = '';
    }
    return r;
  }

  selector(): SelectorChain {
    return ViewMatch.selectorChainFromArray(this.viewSelector);
  }
}

interface Highlight {
  sel: SelectorChain;
  color: string;
}

export interface CanvasRendererOverlayDelegate {
  touchDown(touchid: string | number, x: number, y: number);
  touchUp(touchid: string | number, x: number, y: number);
  positionTouch(touchid: string | number, x: number, y: number);
}

export class CanvasRenderer {
  private container: HTMLElement;
  interpolation: Interpolation = null;
  private webViews: WebViewsState = null;
  private factory: CanvasRendererFactory<number>;
  private frameOffset: number;
  private frameTotal: number;
  private lastFrameTimingLogged: number;
  private framesRendered: number;
  private framesSkipped: number;
  private frameTimeAcc: number;
  private frameTimeMax: number;
  private canvas: HTMLCanvasElement;
  private keyboard: HTMLImageElement;
  private keyboardVisible: boolean;
  private navigationBarPosition: Rect;
  private isIOS: boolean;
  public scale: number;

  // For a given uniqueId we might have two versions of a view when dealing with interpolation
  // the matrix will be null if the view was invisible (and thus not drawn)
  viewMatrices: ObjectIdMap<[[View, number[]]]>;

  // Touches that are currently down
  private touches: ObjectIdMap<any> = {};

  // Latest highlight that should be preserved on render
  private latestHighlight: Highlight | null = null;

  constructor(
    driver: MobileDriver<any>,
    container: HTMLElement,
    canvas: HTMLCanvasElement,
    private _overlays: CanvasRendererOverlayDelegate,
    scale: number,
  ) {
    this.container = container;
    this.canvas = canvas;
    // TODO(mmastrac): Remove this and hoist device-specific behaviour to driver
    this.isIOS = driver.type == PageTypeEnum.PageTypeiOS;
    this.factory = driver.rendererFactory();
    this.keyboard = new Image();
    this.keyboard.src = `${window.location.protocol}//${window.location.host}/s/img/svg/mobile-keyboard-placeholder.svg`;
    this.lastFrameTimingLogged = 0;
    this.framesRendered = this.framesSkipped = 0;
    this.frameTimeAcc = this.frameTimeMax = 0;
    this.scale = scale;
  }

  enablePointerEvents() {
    if (this.canvas.style.pointerEvents !== 'auto') {
      this.canvas.style.pointerEvents = 'auto';
    }
  }

  disablePointerEvents() {
    if (this.canvas.style.pointerEvents !== 'none') {
      this.canvas.style.pointerEvents = 'none';
    }
  }

  width(): number {
    return this.canvas.width / this.scale;
  }

  height(): number {
    return this.canvas.height / this.scale;
  }

  computeWebViewInitialPixelFactor(frameDensity: number): number {
    // TODO(mmastrac): Hoist to driver
    if (this.isIOS) {
      return 1;
    } else {
      // The WebView class and most web browsers on Android convert your CSS pixel values to
      // density-independent pixel values, so your web page appears at the same perceivable
      // size as a medium-density screen (about 160dpi).
      return frameDensity / 160;
    }
  }

  webViewState(): WebViewsState | undefined {
    return this.webViews ?? undefined;
  }

  resetWithError(e: any) {
    console.error('Failed to render frame, doing our best to recover', e);
    // Save the canvas contents as another canvas (can't use an image since the canvas is tainted)
    const saved = document.createElement('canvas');
    saved.width = this.canvas.width;
    saved.height = this.canvas.height;
    saved.getContext('2d').drawImage(this.canvas, 0, 0);
    // Reset the canvas context, since we can't guarantee anything about it
    // eslint-disable-next-line no-self-assign
    this.canvas.width = this.canvas.width;
    // Draw the saved contents
    const c2d = this.canvas.getContext('2d');
    c2d.drawImage(saved, 0, 0);
    // Subtle indication that canvas rendering failed
    c2d.fillText('❌', this.canvas.width / 2, this.canvas.height / 2);
  }

  render(
    interpolation: Interpolation,
    webViews: WebViewsState,
    offset: number,
    total: number,
    skipped: number,
    keyboardVisible: boolean,
  ) {
    this.interpolation = interpolation;
    this.frameOffset = offset;
    this.frameTotal = total;
    this.webViews = webViews;
    this.keyboardVisible = keyboardVisible;
    this.framesRendered++;
    this.framesSkipped += skipped;
    const start = new Date().valueOf();
    try {
      this.processData();
    } catch (e) {
      this.resetWithError(e);
    }
    const end = new Date().valueOf();
    this.frameTimeAcc += end - start;
    this.frameTimeMax = Math.max(end - start, this.frameTimeMax);
    if (start > this.lastFrameTimingLogged + LOGGING_INTERVAL) {
      console.log(
        `Frame rendering time: avg ${this.frameTimeAcc / this.framesRendered}ms, max ${
          this.frameTimeMax
        }ms (sampled from ${this.framesRendered} frames, ${this.framesSkipped} skipped)`,
      );
      this.lastFrameTimingLogged = end;
      this.framesRendered = 0;
      this.framesSkipped = 0;
      this.frameTimeAcc = 0;
      this.frameTimeMax = 0;
    }
  }

  rerender(preserveHighlights = true) {
    this.processData(preserveHighlights);
  }

  boundingBox(view: View): Rect | null {
    const records = this.viewMatrices[view.uniqueId];

    if (records) {
      for (let i = 0; i < records.length; i++) {
        // Blocked views or views with alpha < 0 will be set to null
        if (records[i][0].uniqueId == view.uniqueId && records[i][1] != null) {
          const [x, y] = [records[i][1][2], records[i][1][5]];
          return new Rect(
            x,
            y,
            x + view.position.width() * this.scale,
            y + view.position.height() * this.scale,
          );
        }
      }
    }

    return null;
  }

  highlight(sel: SelectorChain, color: string): number {
    const views = [];
    this.collectViewsRecursive(sel, views, this.interpolation.topView, new SelectorMatcher(), []);

    views.forEach(view => {
      this.highlightSingle(view, color);
    });

    this.latestHighlight = { sel, color };
    return views.length;
  }

  highlightSingle(match: ViewMatch, color: string) {
    const c2d = this.canvas.getContext('2d');
    const view = match.view;
    const records = this.viewMatrices[view.uniqueId];
    if (records) {
      for (let i = 0; i < records.length; i++) {
        if (records[i][0] == view) {
          const matrix = records[i][1];
          if (!matrix) {
            continue; // the view was invisible
          }
          c2d.save();
          Matrix.setMatrix(matrix, c2d);
          c2d.fillStyle = color;
          c2d.globalAlpha = 0.1;
          c2d.fillRect(0, 0, view.position.width(), view.position.height());
          c2d.strokeStyle = color;
          c2d.lineWidth = 2.5;
          c2d.globalAlpha = 1;
          c2d.strokeRect(0, 0, view.position.width(), view.position.height());
          c2d.restore();
          return;
        }
      }
    }
  }

  private collectViewsRecursive(
    sel: SelectorChain,
    views: ViewMatch[],
    view: InterpolatedView,
    matcher: SelectorMatcher,
    viewTree: SelectorPart[],
  ) {
    // Clone and insert this view at the start, if the selector is not hidden.
    const childViewTree = viewTree.slice();
    const selRule = view.view.getSelectorRule();
    if (selRule !== null) {
      childViewTree.push(selRule);
    }
    for (let i = 0; i < view.children.length; i++) {
      this.collectViewsRecursive(sel, views, view.children[i], matcher, childViewTree);
    }

    if (matcher.matches(sel, childViewTree, true)) {
      views.push(new ViewMatch(view.view, childViewTree, 0));
    }
  }

  queryView(uniqueId: ObjectId): ViewMatch | undefined {
    const top = this.interpolation.topView;
    return this.#queryViewRecursive(uniqueId, top, []);
  }

  #queryViewRecursive(
    uniqueId: ObjectId,
    view: InterpolatedView,
    viewTree: SelectorPart[],
  ): ViewMatch | undefined {
    // Clone and insert this view at the start, if the selector is not hidden.
    const childViewTree = viewTree.slice();
    const selRule = view.view.getSelectorRule();
    if (selRule !== null) {
      childViewTree.push(selRule);
    }

    if (view.view.uniqueId === uniqueId) {
      return new ViewMatch(view.view, childViewTree, 0);
    }

    for (const child of view.children) {
      const match = this.#queryViewRecursive(uniqueId, child, childViewTree);
      if (match) return match;
    }

    return undefined;
  }

  querySelectorAll(selector: SelectorChain): ViewMatch[] {
    const top = this.interpolation.topView;
    const matches = [];
    this.collectViewsRecursive(selector, matches, top, new SelectorMatcher(), []);
    return matches;
  }

  computeDescendants(collection: ObjectCollection) {
    // just start at topView since we'd have to recurse regardless to find views by view path unique id
    this.#computeDescendantsRecursive(this.interpolation.topView.view, 0, collection);
  }

  #computeDescendantsRecursive(
    view: View | undefined,
    depth: number,
    collection: ObjectCollection,
  ): ObjectPathDepth[] {
    if (!view) {
      return [];
    }
    const pathString = new ViewPath(view.uniqueId).asString;

    const descendants: ObjectPathDepth[] = [];
    for (const childView of view.children) {
      const childDepth = depth + 1;
      const childPath = new ViewPath(childView.uniqueId).asString;
      // need to add the child view path first if it is an applicable descendant
      if (collection[childPath]) {
        descendants.push({ path: childPath, depth: childDepth });
      }
      // then we can add its associated descendants
      descendants.push(...this.#computeDescendantsRecursive(childView, childDepth, collection));
    }
    const object = collection[pathString];
    if (object) {
      // if the current object is in our collection, we should set the descendants at this point
      object.descendants = descendants.map(child => ({ ...child, depth: child.depth - depth }));
    }
    return descendants;
  }

  viewsAtPoint(pt: Point): ViewMatch[] {
    const top = this.interpolation.topView;
    const topSelector = top.view.getSelectorRule();
    // the top-level android "root" view isn't actually present for rule matching
    // at capture time, so don't present it to users
    const skipTopView =
      !this.isIOS && topSelector && topSelector.tag.toLocaleLowerCase() === 'root';
    const roots = skipTopView ? top.children : [top];
    const matches = [];
    for (const view of roots) this.viewsAtPointRecursive(pt, view, [], matches, new Map());
    matches.reverse();
    return matches;
  }

  private viewsAtPointRecursive(
    pt: Point,
    view: InterpolatedView,
    parents: View[],
    matches: ViewMatch[],
    encounters: Map<string, number>,
  ) {
    const parentsTree = parents.slice();
    parentsTree.push(view.view);

    const viewData = view.view;

    if (viewData.clipmask) {
      logWarnOnce(
        'viewsAtPoint: view has clipping mask that we ignore: expect overshooting selections',
      );
    }

    // The encounters map is used to track the viewMatrices state at the
    // time this view is encountered by processView.
    // This code may be unnecessary because in practice, we appear to only
    // encounter a view once. Nevertheless, we attempt to faithfully follow
    // the same recursion topology as viewMatrices in order to retrieve the
    // appropriate projection matrix at any given recursion point.
    const encounter = encounters.get(view.view.uniqueId) || 0;
    encounters.set(view.view.uniqueId, encounter + 1);
    const records = this.viewMatrices[view.view.uniqueId];
    if (!records) {
      return;
    }
    if (!records[encounter]) {
      logWarnOnce(
        `viewsAtPoint: bad records index ${encounter} (mismatch between viewsAtPoint and render?)`,
      );
      // There is fallback code below but this case should not occur
    }
    if (encounter > 0) {
      // We do not think this situation occurs in practice but would like
      // to verify this by watching for this log message.
      logOnce(
        'viewsAtPoint: found a case in which a view was traversed into twice.  very interesting indeed.',
      );
    }

    // If this view doesn't draw, don't consider it to generate a possible
    // ViewMatch.  On the other hand, WebViews don't have a canvas, though
    // they certainly *do* draw!  Note that even though we can find a
    // WebView in the viewsAtPoint hierarchy, we still can't recur down into
    // that WebView's selectors to inspect them yet.
    if ((viewData.canvas || viewData.isWebView) && records[encounter]) {
      // records[x] is a two element array set up in the viewMatrices
      // variable in the recursive processViews function.
      // records[x][0] is a View object and records[x][1] is the projection
      // matrix at the time processView is entered.
      if (records[encounter][0] != viewData) {
        logWarnOnce('Unexpected view in records array');
      }

      const matrix = records[encounter][1];
      if (matrix == null) {
        // This means that the view is invisible (alpha was 0).
        return;
      }

      let pointInView = true;
      const inverse = Matrix.invert(Matrix.affinize(matrix));
      const transformedPoint = Matrix.transformPoint(inverse, pt.x, pt.y);

      // translate the point (x,y) to the parent's coordinate system
      // so it can be compared with the position and bounds rectangles
      const x = transformedPoint[0] + viewData.position.x1;
      const y = transformedPoint[1] + viewData.position.y1;
      if (!viewData.position.contains(x, y)) {
        // most of the time, children are contained within the bound of their parents,
        // and in those cases we could safely skip the rest of this function,
        // but sometimes (particularly SwiftUI stack views) we need to do more work
        if (viewData.hasChildOutsideBounds && viewData.inclusiveBounds.contains(x, y)) {
          pointInView = false;
        } else {
          return;
        }
      }

      if (pointInView && (viewData.isBlocked || viewData.isWebView)) {
        // just treat blocked views and webviews as simple boxes
        const size = Math.abs(
          viewData.position.width() *
            viewData.position.height() *
            Matrix.determinant(records[encounter][1]),
        );
        matches.push(
          new ViewMatch(
            view.view,
            parentsTree.map(v => v.getSelectorRule()).filter(rule => rule !== null),
            size,
          ),
        );
        return;
      }

      // if this view draws, include it as a match before any of its descendants,
      // which gives it a lower priority in the final list
      for (let i = 0; i < viewData.canvas.length; i++) {
        if (pointInView && this.factory.visibleForInspectMode(viewData.canvas[i])) {
          const size = Math.abs(
            viewData.position.width() *
              viewData.position.height() *
              Matrix.determinant(records[encounter][1]),
          );
          matches.push(
            new ViewMatch(
              view.view,
              parentsTree.map(v => v.getSelectorRule()).filter(rule => rule !== null),
              size,
            ),
          );
          break;
        }
      }
      // even if this view doesn't draw, its descendants might
      for (let i = 0; i < viewData.canvas.length; i++) {
        const potentialChild = this.factory.drawsChild(viewData.canvas[i]);
        if (potentialChild) {
          for (let ch = 0; ch < view.children.length; ch++) {
            if (potentialChild === view.children[ch].view.uniqueId) {
              this.viewsAtPointRecursive(pt, view.children[ch], parentsTree, matches, encounters);
            }
          }
        }
      }
    } else if (!records[encounter]) {
      // If we encounter this case, we are not faithfully simulating the
      // processView recursion strategy.
      logErrorOnce(
        `viewsAtPoint: no viewMatrix ${encounter} for view ${view.view.uniqueId} (mismatch between viewsAtPoint and processView?)`,
      );
    }
  }

  private processData(preserveHighlights = false) {
    this.viewMatrices = {};

    if (!this.interpolation) {
      this.container.innerHTML = '';
      return;
    }

    const rootPosition = this.interpolation.topView.view.position;
    this.canvas.width = rootPosition.width() * this.scale;
    this.canvas.height = rootPosition.height() * this.scale;

    const c2d = this.canvas.getContext('2d');
    c2d.setTransform(this.scale, 0, 0, this.scale, 0, 0);
    c2d.fillStyle = this.isIOS ? 'white' : 'black';
    c2d.fillRect(0, 0, rootPosition.width(), rootPosition.height());

    // this throws away any cached canvas that wasn't used in the last frame
    OffscreenCanvasCache.onNextFrame();

    this.processView(
      this.interpolation.topView,
      null, // the top view has no parent
      this.interpolation.topView,
      c2d,
      Matrix.scale(this.scale, this.scale),
      null,
    );

    // TODO(dave): we should set a root bg with images of the status and nav bars
    // (and shoot for trying to render their states dynamically).
    // For now just keeping the 'outer bands' black for aesthetics.
    const win = this.interpolation.getWindow();
    c2d.fillStyle = 'black';
    c2d.fillRect(0, 0, win.width(), win.y1);
    c2d.fillRect(0, win.y2, win.width(), this.interpolation.topView.view.position.width());

    // TODO(mmastrac): Hokey way to do it
    let height = rootPosition.height();
    if (this.navigationBarPosition) {
      height = this.navigationBarPosition.height();
    }

    // This is only for Android's keyboard (iOS's keyboard is drawn via a special canvas op)
    // TODO(mmastrac): This should be replaced by an intelligent keyboard fill-in that can scale to any size
    if (this.keyboardVisible) {
      c2d.drawImage(this.keyboard, 0, win.height(), win.width(), height - win.height());
    }

    for (let i = this.container.children.length - 1; i >= 0; i--) {
      const child = this.container.children[i];
      if (child.className == 'key' || child.className == 'touch') {
        this.container.removeChild(child);
      }
    }

    if (this.interpolation.keys && this.interpolation.keys[0] == 4) {
      const div = document.createElement('div');
      div.className = 'key';
      div.textContent = 'back';
      this.container.firstChild.appendChild(div);
    }

    const currentTouches = {};
    const firstHalf = this.frameOffset < this.frameTotal / 2;

    // Only show touches in the last 250ms of a long gap - this prevents smearing of a touch when idle
    if (this.frameTotal < 500 || this.frameTotal - this.frameOffset < 250) {
      const touches = this.interpolation.touches;

      for (let i = 0; i < touches.length; i++) {
        const touch = touches[i];
        const touchPos = touch.touchAt(this.interpolation.fraction);
        // eslint-disable-next-line no-var
        var x = touchPos.x;
        // eslint-disable-next-line no-var
        var y = touchPos.y;
        const touchId = touch.id;

        const touchVisible =
          (touch.isNew && !firstHalf) ||
          (!touch.isDown && firstHalf) ||
          (!touch.isNew && touch.isDown);

        if (touchVisible && !currentTouches[touchId]) {
          if (!this.touches[touchId]) {
            this._overlays.touchDown(touchId, x, y);
            this.touches[touchId] = true;
          } else {
            this._overlays.positionTouch(touchId, x, y);
          }
          currentTouches[touchId] = true;
        }
      }
    }

    // Remove any stale touches
    Object.getOwnPropertyNames(this.touches).forEach(key => {
      if (!currentTouches[key]) {
        this._overlays.touchUp(key, x, y);
        delete this.touches[key];
      }
    });

    // And re-highlight anything that was highlighted if we're preserving highlights
    if (preserveHighlights && this.latestHighlight) {
      this.highlight(this.latestHighlight.sel, this.latestHighlight.color);
    } else {
      this.latestHighlight = null;
    }
  }

  private notifyView(
    view: View,
    c2d: CanvasRenderingContext2D,
    currentMatrix: number[],
    msg: string,
  ) {
    logWarnOnce(`WebView ${view.asSelector()} (id=${view.uniqueId}): ${msg}`);
    c2d.save();
    Matrix.setMatrix(currentMatrix, c2d);
    const pattern = c2d.createPattern(BLOCKED_IMG, 'repeat');
    c2d.fillStyle = pattern;
    c2d.globalCompositeOperation = 'source-over';
    c2d.beginPath();
    c2d.rect(0, 0, view.position.width(), view.position.height());
    c2d.clip();
    c2d.fillRect(0, 0, view.position.width(), view.position.height());

    // Measure our string
    c2d.font = '20px Roboto';
    const metrics = c2d.measureText(msg);

    // todo: wrap the text instead of shrinking it - or at least support line breaks

    // Now scale it to fit 75% of the viewport
    const scale = (view.position.width() * 0.75) / metrics.width;
    c2d.scale(scale, scale);
    c2d.fillStyle = 'black';

    // Render it centered
    c2d.shadowColor = 'white';
    c2d.shadowOffsetX = 2;
    c2d.shadowOffsetY = 2;
    c2d.fillText(
      msg,
      view.position.width() / scale / 2 - metrics.width / 2,
      view.position.height() / 2 / scale,
    );

    c2d.restore();
  }

  /**
   * Render blocked patteren around blocked mobile views
   */
  private blockedMobileContent(view: View, c2d: CanvasRenderingContext2D, currentMatrix: number[]) {
    c2d.save();
    Matrix.setMatrix(currentMatrix, c2d);
    const pattern = c2d.createPattern(BLOCKED_IMG, 'repeat');
    c2d.fillStyle = pattern;
    /**
     * Thou shall not edit, for thy divine stack overflow has given us the answers!
     * When we used fillRect we saw noticeable performance stutter in playback where
     * a render cycle time increased 100 fold! But fear not! The culprit was a fillRect,
     * which the divine gave us a performant replacement!
     *
     * Culprit: c2d.fillRect(0, 0, view.position.width(), view.position.height());
     */
    c2d.beginPath();
    c2d.rect(0, 0, view.position.width(), view.position.height());
    c2d.fill();
    c2d.closePath();
    c2d.restore();
  }

  private processView(
    root: InterpolatedView,
    parent: CanvasState | null,
    viewData: InterpolatedView,
    c2d: CanvasRenderingContext2D,
    matrix: number[],
    relativeMatrix: number[] | null, // relative to the passed-in canvas, if it's offscreen
  ) {
    const view: View = viewData.view;
    if (!view.isBlocked && view.alpha <= 0) {
      // we don't record alpha for blocked views, because we don't record anything, but we still want to render a cross-hatch pattern there
      // however, we still need to record a null matrix to inform viewsAtPoint that the view is invisible
      if (!this.viewMatrices[view.uniqueId]) {
        this.viewMatrices[view.uniqueId] = [[view, null]];
      } else {
        logWarnOnce(`processView: adding empty viewMatrix for ${view.uniqueId}`);
        this.viewMatrices[view.uniqueId].push([view, null]);
      }
      return;
    }

    // SAVE: processView
    c2d.save();

    let left, top;

    if (viewData.interpolation.position) {
      const position = viewData.interpolation.position;
      left = this.interpolation.linear(position.from[0], position.to[0]);
      top = this.interpolation.linear(position.from[1], position.to[1]);
    } else {
      left = view.position.x1;
      top = view.position.y1;
    }

    let currentMatrix = Matrix.multiply(Matrix.translation(left, top), matrix);
    let currentRelativeMatrix: number[] | null = null;
    if (relativeMatrix)
      currentRelativeMatrix = Matrix.multiply(Matrix.translation(left, top), relativeMatrix);
    // TODO(mmastrac): Special views - ie: keyboard/navigation/status bar/etc
    // if (view.viewId == -2) {
    //   //div.setAttribute('view-id', 'content-view');
    // }
    // else if (view.viewId == -3) {
    //   //div.setAttribute('view-id', 'status-bar-view');
    // }
    // else if (view.viewId == -4) {
    //   this.navigationBarPosition = view.position;
    //   //div.setAttribute('view-id', 'navigation-bar-view');
    // } else if (view.viewId != -1) {
    //   //div.setAttribute('view-id', "" + view.viewId);
    // }

    let m = view.matrix;
    if (m) {
      m = Matrix.affinize(m);
      currentMatrix = Matrix.multiply(m, currentMatrix);
      if (currentRelativeMatrix) currentRelativeMatrix = Matrix.multiply(m, currentRelativeMatrix);
    }
    let childRelativeMatrix = currentRelativeMatrix;

    if (!this.viewMatrices[view.uniqueId])
      this.viewMatrices[view.uniqueId] = [[view, currentMatrix]];
    else {
      logWarnOnce(`processView: adding another viewMatrix for ${view.uniqueId}`);
      this.viewMatrices[view.uniqueId].push([view, currentMatrix]);
    }

    const isIosKeyboard =
      this.isIOS &&
      view.isBlocked &&
      view.canvas.length === 1 &&
      view.canvas[0].op === CanvasOpIOS.DRAW_KEYBOARD;

    if (
      view.isBlocked &&
      !view.isWebView && // special combo used for 'ignored webview'
      !isIosKeyboard // iOS keyboard is blocked, but we want to draw a placeholder instead
    ) {
      this.blockedMobileContent(view, c2d, currentMatrix);
    } else if (view.isWebView) {
      this.renderWebView(view, c2d, currentMatrix);
    }

    if (view.canvas?.length) {
      /* XXX: Does this incorrectly apply alpha repeatedly to child views?
       *
       * XXX: initial implementation of this tested for
       * view.hasComplexCompositingChild instead of view.filter; we should
       * have a test to know for sure.  (One challenge is that there may
       * be no public API to do that, though; Apple say that
       * compositingFilter is not an officially supported thing to do on
       * iOS, despite that their own spinner control and keyboard do
       * it...) Note that this does not work for CAFilter.xor if we use
       * hasComplexCompositingChild as the key, instead of view.filter --
       * or, indeed, if even we add hasComplexCompositingChild in an OR in
       * the term for offscreen here.
       */
      const offscreen = view.alpha < 1 || view.filter;

      let viewC2d;
      if (offscreen) {
        let width = view.inclusiveBounds.width();
        let height = view.inclusiveBounds.height();
        viewC2d = OffscreenCanvasCache.getC2d(width * this.scale, height * this.scale);
        // the inclusive bounds might extend above or to the left
        const translate = Matrix.translation(-view.inclusiveBounds.x1, -view.inclusiveBounds.y1);
        childRelativeMatrix = Matrix.multiply(translate, Matrix.scale(this.scale, this.scale));
        Matrix.setMatrix(childRelativeMatrix, viewC2d);
      } else {
        viewC2d = c2d;
      }

      const ops = view.canvas;
      const state = new CanvasState(
        view.position.width(),
        view.position.height(),
        view,
        viewC2d,
        parent,
        (viewId, matrix) => {
          // Note that we may have two children with the same ID due to interpolation
          let found = 0;
          for (let i = 0; i < viewData.children.length; i++) {
            if (viewData.children[i].view.uniqueId === viewId) {
              this.processView(
                root,
                state,
                viewData.children[i],
                viewC2d,
                Matrix.multiply(matrix, currentMatrix),
                childRelativeMatrix ? Matrix.multiply(matrix, childRelativeMatrix) : null,
              );
              found++;
            }
          }

          if (found == 0) {
            // TODO(mmastrac): This might need some verification that nothing bad is going on
            logWarnOnce(
              `viewrenderer: Uh-oh: missing potentially-clipped child ${viewId} from parent ${
                view.uniqueId
              } (${view.asSelector()})`,
            );
          }
        },
      );

      // SAVE: if (view.canvas)
      c2d.save();

      // We could be using the canvas to track the current matrix, but this allows us to ensure that our
      // internal accounting is correct.
      // if the relative matrix is present, this isn't the base canvas, so we shouldn't use the absolute transform
      Matrix.setMatrix(currentRelativeMatrix || currentMatrix, c2d);

      // Blur or dim behind
      if (view.windowFlags & 2 || view.windowFlags & 4) {
        c2d.save();
        c2d.setTransform(1, 0, 0, 1, 0, 0);
        c2d.fillStyle = 'rgba(0, 0, 0, 0.4)';
        c2d.fillRect(0, 0, c2d.canvas.width, c2d.canvas.height);
        c2d.restore();
      }

      // iOS has the ability for layers to set custom compositing filters. CAFilter is technically a private API
      // so it isn't well documented what it does, but this seems to have the same effect.
      // TODO(mmastrac): May need to test to see how this interacts with parent layers
      let reqcomposite = null;

      if (view.filter == 'CAFilter.destIn') {
        reqcomposite = 'destination-in';
      } else if (view.filter == 'CAFilter.destOut') {
        reqcomposite = 'destination-out';
      } else if (view.filter == 'CAFilter.colorBurnBlendMode') {
        reqcomposite = 'color-burn';
      } else if (view.filter == 'CAFilter.lightenSourceOver') {
        reqcomposite = 'lighten';
      } else if (view.filter == 'CAFilter.darkenSourceOver') {
        reqcomposite = 'darken';
      } else if (view.filter == 'CAFilter.xor') {
        reqcomposite = 'xor';
      } else if (view.filter == 'CAFilter.plusD') {
        reqcomposite = 'plus-darker';
      } else if (view.filter) {
        logWarnOnce(
          `viewrenderer: Unsupported composite operation ${view.filter} on view ${
            view.uniqueId
          } (${view.asSelector()})`,
        );
      }

      if (reqcomposite) {
        c2d.globalCompositeOperation = reqcomposite;
        if (c2d.globalCompositeOperation != reqcomposite) {
          logWarnOnce(
            `viewrenderer: Browser does not support composite mode ${reqcomposite} found on view ${
              view.uniqueId
            } (${view.asSelector()})`,
          );
        }
      }

      // Android doesn't set clipping a rectangle yet, so when we decide to clip we just set the entire view's bounds.
      //
      // For now, set the clipping rect only when the View is set to clip its children. There are more flags we can
      // utilize in the future if we need to (like Flags.VIEW_GROUP_CLIP_TO_PADDING).
      if (
        !this.isIOS &&
        parent &&
        parent.view &&
        parent.view.flag(Flags.VIEW_GROUP_CLIP_CHILDREN)
      ) {
        c2d.beginPath();
        c2d.rect(0, 0, view.position.width(), view.position.height());
        c2d.clip();
      }
      if (view.clipmask) {
        c2d.beginPath();
        c2d.rect(view.clipmask.x1, view.clipmask.y1, view.clipmask.width(), view.clipmask.height());
        c2d.clip();
      }

      // TODO(mmastrac): Not a fan of this... would prefer to keep this detail in the canvas renderer
      if (this.isIOS) {
        viewC2d.beginPath();
        viewC2d.lineWidth = 1;
        viewC2d.lineCap = 'butt';
        viewC2d.lineJoin = 'miter';
        viewC2d.miterLimit = 10;
        viewC2d.fillStyle = 'black';
        viewC2d.strokeStyle = 'black';
      }

      for (let j = 0; j < ops.length; j++) {
        if (viewData.interpolation.translate && viewData.interpolation.translate.index == j) {
          const canvas = new CanvasAndroid();
          canvas.op = CanvasOpAndroid.TRANSLATE;
          canvas.point = new Point(
            this.interpolation.linear(
              viewData.interpolation.translate.from.x,
              viewData.interpolation.translate.to.x,
            ),
            this.interpolation.linear(
              viewData.interpolation.translate.from.y,
              viewData.interpolation.translate.to.y,
            ),
          );
          this.factory.render(state, canvas);
          if (!viewData.interpolation.translate.insert) {
            continue;
          }
        }

        this.factory.render(state, ops[j]);
      }
      state.finish();

      if (offscreen) {
        c2d.globalAlpha = view.alpha;
        if (!view.inclusiveBounds.isEmpty()) {
          c2d.drawImage(
            viewC2d.canvas,
            view.inclusiveBounds.x1,
            view.inclusiveBounds.y1,
            view.inclusiveBounds.width(),
            view.inclusiveBounds.height(),
          );
        }
        OffscreenCanvasCache.putC2d(viewC2d);
        viewC2d = null;
      }

      // SAVE: if (view.canvas)
      c2d.restore();
    }

    // SAVE: processView
    c2d.restore();
    //
    // if (view.viewId == -4) {
    //  div.style.width = view.position.width() + 'px';
    //  div.style.height = view.position.height() + 'px';
    //  for (var j = 0; j < 3; j++) {
    //    var btn = document.createElement('div');
    //    btn.className = 'navigation-' + j;
    //    div.appendChild(btn);
    //  }
    // }

    // parent.appendChild(div);
    // return div;
  }

  // Webviews are actually rendered in _updateIframes in renderer/src/mobile/renderer.ts
  private renderWebView(view: View, c2d: CanvasRenderingContext2D, currentMatrix: number[]) {
    const state = this.webViews.views[view.uniqueId];
    if (view.isBlocked) {
      // Note: the FullStory framework now uses reasonNotRecorded rather than marking the view as blocked,
      // but playback needs to also support older framework releases, so this check stays for now.
      const msg = intl.formatMessage({
        defaultMessage: '❌ WebView not captured due to fullstoryInjectionDisabled setting.',
        id: 'IJ1N/D',
      });
      this.notifyView(view, c2d, currentMatrix, msg);
    } else if (!state) {
      const msg = intl.formatMessage({
        defaultMessage: '❌ Internal Error: WebView not captured.',
        id: 'eCzbBf',
      });
      this.notifyView(view, c2d, currentMatrix, msg);
    } else if (state.reasonNotRecorded !== null && state.reasonNotRecorded !== undefined) {
      let msg: string;
      switch (state.reasonNotRecorded) {
        case FSWebviewNotRecordedReason.DOMAIN_NOT_ALLOWLISTED:
          msg = intl.formatMessage(
            {
              defaultMessage: '⚠️ {url} was not allowlisted.',
              id: 'CMlUH3',
            },
            {
              url: state.url,
            },
          );
          break;
        case FSWebviewNotRecordedReason.INJECTION_DISABLED:
          msg = intl.formatMessage({
            defaultMessage: '⚠️ WebView not captured due to fullstoryInjectionDisabled setting.',
            id: 'ag8MSr',
          });
          break;
        case FSWebviewNotRecordedReason.JAVASCRIPT_DISABLED:
          msg = intl.formatMessage({
            defaultMessage: '⚠️ Webview not captured due to JavaScript being disabled.',
            id: 'e0Ax5t',
          });
          break;
        case FSWebviewNotRecordedReason.SAFARI_VIEW_CONTROLLER:
          msg = intl.formatMessage({
            defaultMessage: '⚠️ SFSafariViewController WebViews cannot be captured.',
            id: 'ZK/ixj',
          });
          break;
        case FSWebviewNotRecordedReason.WKWEBVIEW_USERSCRIPTS_REMOVED:
          msg = intl.formatMessage({
            defaultMessage:
              '⚠️ Application called -[WKUserContentController removeAllUserScripts].',
            id: 'SBuNZf',
          });
          break;
        default:
          logWarnOnce(
            `Unsupported FSWebviewNotRecordedReason: ${
              FSWebviewNotRecordedReason[state.reasonNotRecorded]
            } (${state.reasonNotRecorded}) on WebView ${view.asSelector()} (id=${view.uniqueId})`,
          );
          msg = intl.formatMessage({ defaultMessage: '⚠️ WebView was not captured', id: 'ojqgWg' });
      }
      this.notifyView(view, c2d, currentMatrix, msg);
    } else {
      // Punch a hole in the rendering so we can include a web renderer
      c2d.save();
      Matrix.setMatrix(currentMatrix, c2d);
      c2d.fillStyle = 'transparent';
      c2d.globalCompositeOperation = 'copy';
      const width = view.position.width();
      const height = view.position.height();
      // This clip operation is required - it *shouldn't* be since it mirrors the fillRect below, but if
      // we don't do this, our test app fails to render the toolbar about the webview.
      c2d.beginPath();
      c2d.rect(0, 0, width, height);
      c2d.clip();
      c2d.fillRect(0, 0, width, height);
      // ch162519: more work that *shouldn't* be required.
      // When we make part of a canvas transparent, sometimes Chrome will incorrectly "remember" what was there in the previous frame.
      // So, if there's an overlay there for one frame and not the next, Chrome may incorrectly display the overlay in the next frame.
      // Asking it to read back the color of a pixel seems to be enough to cause the browser to correct it's mistake.
      c2d.getImageData(Math.floor(width / 2), Math.floor(height / 2), 1, 1);
      c2d.restore();
    }
  }
}
